import React, { useEffect, useMemo, useRef } from 'react';
import { Timeline as VisTimeline } from 'vis-timeline/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';

import { Card, Flex, Text as TextTremor } from './TremorCompat';
import { Button } from './TremorCompat';

import CsvLink from './CsvLink';

import { build_fault_string, SelectDateRange, getDateTimeFromRecordID } from './Common';
// import DateRangePicker from "rsuite/DateRangePicker";

import { FaultsDisplayTable } from './FaultsTable';

import { COLOURS, headingStyle } from '../styles';
import {
  datetimeFormats,
  // fault_strings_idx,
} from '../constants';

import { lightFormat } from 'date-fns';
import { LoadingView } from './DisplayItems';
import { useDeviceContext } from '../contexts/DeviceContext';
import { devLog } from '../utils/devLog';

// record.colour comes from build_fault_string, which sets it to a COLOURS
// token — a Tailwind colour NAME (e.g. "green" for cleared, "yellow" for a
// fault), not a hex string. Map those names to hex so vis-timeline (which
// needs a CSS colour) renders them; previously this map was keyed by hex, so
// every lookup missed and every item fell back to slate, losing all colour.
const tailwindColorMap = {
  green: '#22c55e', // green-500
  yellow: '#eab308', // yellow-500
  red: '#ef4444', // red-500
  orange: '#f59e42', // orange-400
  amber: '#f59e0b', // amber-500
  blue: '#3b82f6', // blue-500
  sky: '#0ea5e9', // sky-500
  indigo: '#6366f1', // indigo-500
  violet: '#8b5cf6', // violet-500
  purple: '#a855f7', // purple-500
  lime: '#84cc16', // lime-500
  pink: '#ec4899', // pink-500
  emerald: '#10b981', // emerald-500
  neutral: '#737373', // neutral-500
};

const getTailwindColor = (colour) => {
  // Accept a hex value as-is (some COLOURS tokens are already hex).
  if (typeof colour === 'string' && colour.startsWith('#')) return colour;
  return tailwindColorMap[colour] || '#64748b'; // fallback to slate-500
};

export const HistoryView = () => {
  const {
    selectedDateRange,
    historyFaults,
    selectedDevice,
    loading,
    progress,
    refreshFaultDataByDate: onSelectedFaultDateRangeChanged,
  } = useDeviceContext();
  const [noData, setNoData] = React.useState(false);
  const [formattedHistoryData, setFormattedHistoryData] = React.useState([]);

  const refreshFaultView = () => {
    devLog('refresh fault view clicked', selectedDateRange);
    onSelectedFaultDateRangeChanged(selectedDateRange);
  };

  const formatHistoryFaults = (faultsToFormat) => {
    var formatted = [];
    var itr = 1;
    var tempFaultRecord = {};
    devLog('formatting History Faults');
    faultsToFormat.forEach((faultRecord) => {
      tempFaultRecord = {};
      let date_time = getDateTimeFromRecordID(faultRecord.recordID);
      if (date_time === undefined) {
        devLog('skipping fault record with unparseable recordID', faultRecord.recordID);
        return;
      }
      tempFaultRecord.dateTime = date_time.toISOString(); // Use ISO string for vis-timeline
      // tempFaultRecord.faults = faultRecord.faults;
      let largeValue = parseInt(faultRecord.faults, 10);

      let built_fault_string = build_fault_string(largeValue, false, 'All Faults Cleared');

      tempFaultRecord.faults = built_fault_string.string;
      tempFaultRecord.colour = built_fault_string.colour;
      tempFaultRecord.errorCode = faultRecord.errorCode;
      tempFaultRecord.version = faultRecord.version;
      tempFaultRecord.id = itr;
      tempFaultRecord.value = largeValue;
      tempFaultRecord.dateTimeDisplay = lightFormat(date_time, datetimeFormats.long); // For display only
      itr++;
      devLog('built up a fault record', faultRecord, date_time, tempFaultRecord, built_fault_string, largeValue);
      formatted.push(tempFaultRecord);
    });
    // console.log("Formatted:", formatted);
    setFormattedHistoryData(formatted);
  };

  useEffect(() => {
    devLog('history Faults Changed - ', historyFaults);

    setNoData(historyFaults.length === 0 ? true : false);

    formatHistoryFaults(historyFaults);
  }, [historyFaults]);

  const timelineRef = useRef(null);
  const visTimelineInstance = useRef(null);

  // Prepare vis-timeline groups and items
  const groups = useMemo(
    () =>
      Array.from(new Set(formattedHistoryData.map((r) => r.colour))).map((code) => ({
        id: code,
        content: `Error ${code}`,
      })),
    [formattedHistoryData],
  );

  const items = useMemo(
    () =>
      formattedHistoryData.map((record) => ({
        id: record.id,
        group: record.colour, // Use colour as group ID
        content: record.faults,
        start: record.dateTime, // Now an ISO string, always parsed correctly
        end: undefined,
        style: `background:${getTailwindColor(record.colour)};color:#fff;`,
      })),
    [formattedHistoryData],
  );

  // Initialize or update the vis-timeline
  useEffect(() => {
    if (timelineRef.current) {
      if (visTimelineInstance.current) {
        visTimelineInstance.current.setItems(items);
        visTimelineInstance.current.setGroups(groups);
      } else {
        visTimelineInstance.current = new VisTimeline(timelineRef.current, items, groups, {
          stack: true,
          height: '500px',
          margin: { item: 10 },
          orientation: 'top',
          showCurrentTime: true,
          zoomMin: 1000 * 60 * 60, // 1 hour
          zoomMax: 1000 * 60 * 60 * 24 * 30, // 1 month
        });
      }
    }
    // Cleanup on unmount
    return () => {
      if (visTimelineInstance.current) {
        visTimelineInstance.current.destroy();
        visTimelineInstance.current = null;
      }
    };
    // Only re-run if items or groups change
  }, [items, groups]);

  return (
    <div className="mt-3">
      <Flex justifyContent="center" alignItems="center" className="space-x-2">
        <SelectDateRange
          range={selectedDateRange}
          maxDays={30}
          cleanable={false}
          onChange={onSelectedFaultDateRangeChanged}
          style={headingStyle}
        />
        <Button
          color={COLOURS.default}
          size="sm"
          onClick={refreshFaultView}
          disabled={selectedDevice.length === 0 || loading}
        >
          Refresh
        </Button>
      </Flex>

      {loading ? (
        <LoadingView progress={progress} />
      ) : selectedDevice.length === 0 ? (
        <Card decoration="bottom" decorationColor={COLOURS.cardDecoration} className="mt-3">
          <TextTremor color={COLOURS.warning}>Select a controller</TextTremor>
        </Card>
      ) : noData === true ? (
        <TextTremor color={COLOURS.good}>No faults found</TextTremor>
      ) : (
        <div>
          <Flex justifyContent="end" alignItems="center" className="space-x-2" style={{ marginBottom: 12 }}>
            <CsvLink
              text="Download"
              data={formattedHistoryData}
              headers={['id', 'dateTime', 'faults', 'errorCode', 'version']}
              device={selectedDevice}
              itemType="faults"
              format={datetimeFormats.long}
            />
          </Flex>

          <FaultsDisplayTable formattedHistoryFaults={formattedHistoryData} />
          <div style={{ height: 500, marginTop: 16 }}>
            <div ref={timelineRef} />
          </div>
        </div>
      )}
    </div>
  );
};
